import { WorkspacePanel } from './workspace-panel';
import { WorkspacePanelDto } from '@mark43/cad-api';


//open new panel with padding from the edge
export const DEFAULT_PANEL_X_POSITION = 16;
export const DEFAULT_PANEL_Y_POSITION = 16;

const PANEL_CASCADE_OFFSET = 25;
const PREFERRED_PANEL_PADDING = 20;
type PanelPosition = { xcoordinate: number; ycoordinate: number; heightOverride?: number };

/**
 * This algorithm positions a new panel in relation to the currently focused panel. It implements the following heuristics/rules
 *
 * X-Axis Positioning
 * - Generally, prefer to place new panels in an open space that would fit the panel.
 * - If there is not adequate space:
 *   - Determine the panel which is bottommost, and create the new panel with an X offset of 50 pixels.
 *
 * Y-Axis Positioning
 * - If the panel is being opened to the right, maintain the y-axis value from the last panel.
 * - If the panel is cascaded, it should be 50 pixels lower than the relative panel.
 * - The panel must never overflow off the bottom of the screen. Shorten the height of the panel such that the corner
 *   positioning is correct but the bottom of the panel aligns with the bottom of the window.
 */
export function getNewPanelPosition(
    allPanels: WorkspacePanel[],
    panelArea: { width: number; height: number },
    newPanelWidthAndHeight: { width: number; height: number },
    type: WorkspacePanelDto['workspacePanelType']
): PanelPosition {
    const isNotQueueOrEventPanel =
        type !== 'EVENT' && type !== 'SCHEDULED_EVENT' && type !== 'QUEUE' && type !== 'QUEUE_FORM';

    if (isNotQueueOrEventPanel) {
        return { xcoordinate: DEFAULT_PANEL_X_POSITION, ycoordinate: DEFAULT_PANEL_Y_POSITION };
    }

    const panelsToUse = allPanels.filter(
        (p) =>
            p.workspacePanelType === 'EVENT' ||
            p.workspacePanelType === 'SCHEDULED_EVENT' ||
            p.workspacePanelType === 'QUEUE' ||
            p.workspacePanelType === 'QUEUE_FORM'
    );

    const emptyAreaForPanel = getEmptyAreaForNewPanel(
        panelsToUse,
        panelArea,
        newPanelWidthAndHeight
    );
    return (
        emptyAreaForPanel ??
        getStaggeredPanelPosition(panelsToUse, panelArea, newPanelWidthAndHeight)
    );
}

function getEmptyAreaForNewPanel(
    allPanels: WorkspacePanel[],
    panelArea: { width: number; height: number },
    newPanelWidthAndHeight: { width: number; height: number }
): PanelPosition | undefined {
    const occupiedArea = allPanels.map((p) => ({
        startX: p.xcoordinate,
        endX: p.xcoordinate + p.width,
    }));

    occupiedArea.sort((a, b) => a.startX - b.startX);

    let lastOccupiedArea = undefined;

    for (let i = 0; i < occupiedArea.length; i++) {
        const currentArea = occupiedArea[i];
        const isLastPanel = i === occupiedArea.length - 1;

        // If we're at the last panel, we want to check the remaining space on the screen.
        // Sometimes there's only one panel, so do this first.
        if (isLastPanel) {
            const remainingSpaceInPanelArea = panelArea.width - currentArea.endX;
            if (remainingSpaceInPanelArea >= newPanelWidthAndHeight.width) {
                return {
                    xcoordinate: currentArea.endX + PREFERRED_PANEL_PADDING,
                    ycoordinate: DEFAULT_PANEL_Y_POSITION,
                };
            }
        }

        if (lastOccupiedArea === undefined) {
            lastOccupiedArea = currentArea;
            continue;
        }

        // Check if the gap between the last panel and the current panel is large enough to fit a new one.
        const xcoordinateOfCurrentPanel = currentArea.startX;
        const rightEdgeOfLastPanel = lastOccupiedArea.endX;
        const freeSpaceBetweenPanels = xcoordinateOfCurrentPanel - rightEdgeOfLastPanel;
        const newPanelWouldGoOutOfBounds =
            rightEdgeOfLastPanel + newPanelWidthAndHeight.width > panelArea.width;

        if (!newPanelWouldGoOutOfBounds && newPanelWidthAndHeight.width <= freeSpaceBetweenPanels) {
            return { xcoordinate: rightEdgeOfLastPanel + PREFERRED_PANEL_PADDING, ycoordinate: DEFAULT_PANEL_Y_POSITION };
        }

        lastOccupiedArea = currentArea;
    }
}

function getStaggeredPanelPosition(
    allPanels: WorkspacePanel[],
    panelArea: { width: number; height: number },
    newPanelWidthAndHeight: { width: number; height: number }
): PanelPosition {
    const anchorPanel = getBottommostPanel(allPanels);

    if (!anchorPanel) {
        return {
            xcoordinate: DEFAULT_PANEL_X_POSITION,
            ycoordinate: DEFAULT_PANEL_Y_POSITION,
        };
    }

    const emptySpaceBelowAnchorPanel = Math.round(
        panelArea.height -
            (anchorPanel.ycoordinate + PANEL_CASCADE_OFFSET) -
            newPanelWidthAndHeight.height
    );

    // Shrink the panel vertically so it doesn't stack directly on top of the last one.
    const shouldMakeNewPanelSmaller = emptySpaceBelowAnchorPanel <= 0;

    return {
        xcoordinate: anchorPanel ? anchorPanel.xcoordinate + PANEL_CASCADE_OFFSET : DEFAULT_PANEL_X_POSITION,
        ycoordinate: anchorPanel ? anchorPanel.ycoordinate + PANEL_CASCADE_OFFSET : DEFAULT_PANEL_Y_POSITION,
        heightOverride: shouldMakeNewPanelSmaller
            ? newPanelWidthAndHeight.height - Math.abs(emptySpaceBelowAnchorPanel)
            : newPanelWidthAndHeight.height,
    };
}

function getBottommostPanel(panels: WorkspacePanel[]): WorkspacePanel | undefined {
    let anchorPanel: WorkspacePanel | undefined = undefined;

    for (const panel of panels) {
        if (!anchorPanel) {
            anchorPanel = panel;
        }

        if (panel.ycoordinate > anchorPanel.ycoordinate) {
            anchorPanel = panel;
        }
    }

    return anchorPanel;
}
